import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { ScrollToTopOnMount } from "frame/components";
import StaffAddEditForm from "./staff-add-edit-container";
import StaffPinnedItems from "./staff-pinned-items";
import StaffInfoCard from "./staff-info-card";
import StaffTeamPanel from "./staff-team-panel";
import StaffPhotosNotesPanel from "./staff-photos-notes-panel";
import StaffTrainingPanel from "./staff-training-panel";
import {
	isConsultantUser,
	isBornAccount,
	canManageStaff,
	canPinJobs,
	getVisibleStaffDirectoryIds,
	isInStaffDirectory,
} from "utils";

const useStyles = makeStyles((theme) => ({
	sectionSlot: {
		margin: theme.spacing(2, 2, 0, 2),
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1, 1.5, 0, 1.5),
		},
	},
}));

const STAFF_SECTION_INFO = "info";
const STAFF_SECTION_TEAM = "team";
const STAFF_SECTION_PHOTOS_NOTES = "photosNotes";
const STAFF_SECTION_TRAINING = "training";

const Summary = ({
	person,
	organisationId,
	profile,
	organisations,
	staffList,
	onEdit,
	onAddPhotoNote,
}) => {
	const classes = useStyles();
	const [expandedSection, setExpandedSection] = useState(STAFF_SECTION_INFO);

	const onInfoExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? STAFF_SECTION_INFO : null);
	}, []);

	const onTeamExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? STAFF_SECTION_TEAM : null);
	}, []);

	const onPhotosNotesExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? STAFF_SECTION_PHOTOS_NOTES : null);
	}, []);

	const onTrainingExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? STAFF_SECTION_TRAINING : null);
	}, []);

	if (!person) {
		return null;
	}

	const currentStaffId = profile.staffId;
	const isCreator = (staff) => {
		const organisation = organisations.filter(
			(node) => node.creatorId === staff.id
		);
		return !!organisation.length;
	};
	const editable = (staff) =>
		currentStaffId !== staff.id ? !isCreator(staff) : true;

	const showEdit =
		canManageStaff(profile.role) &&
		!isConsultantUser(person.organisationRole) &&
		editable(person) &&
		!isBornAccount(profile.email);
	const allowUnpin = canPinJobs(profile.role);
	const directoryIds = getVisibleStaffDirectoryIds(
		profile.role,
		profile.supervisingStaffList
	);
	const teamStaffList =
		directoryIds == null
			? staffList
			: (staffList || []).filter((member) =>
					isInStaffDirectory(directoryIds, member.id)
			  );

	const supervisor =
		staffList && staffList.filter((s) => s.id === person.supervisor)[0];
	const supervisorName = supervisor ? supervisor.fullName : "-";

	const knownPinnedCount =
		typeof person.pinnedJobCount === "number"
			? person.pinnedJobCount
			: undefined;

	return (
		<>
			<ScrollToTopOnMount />
			<StaffAddEditForm />
			<StaffPinnedItems
				staffId={person.id}
				organisationId={organisationId}
				organisationRole={person.organisationRole}
				knownPinnedCount={knownPinnedCount}
				allowUnpin={allowUnpin}
			/>
			<div className={classes.sectionSlot}>
				<StaffInfoCard
					person={person}
					supervisorName={supervisorName}
					expanded={expandedSection === STAFF_SECTION_INFO}
					onExpandedChange={onInfoExpanded}
					showEdit={showEdit}
					onEdit={onEdit}
				/>
			</div>
			<div className={classes.sectionSlot}>
				<StaffTeamPanel
					staffId={person.id}
					staffList={teamStaffList}
					expanded={expandedSection === STAFF_SECTION_TEAM}
					onExpandedChange={onTeamExpanded}
				/>
			</div>
			<div className={classes.sectionSlot}>
				<StaffPhotosNotesPanel
					person={person}
					organisationId={organisationId}
					expanded={expandedSection === STAFF_SECTION_PHOTOS_NOTES}
					onExpandedChange={onPhotosNotesExpanded}
					onAddPhotoNote={onAddPhotoNote}
				/>
			</div>
			<div className={classes.sectionSlot}>
				<StaffTrainingPanel
					staffId={person.id}
					expanded={expandedSection === STAFF_SECTION_TRAINING}
					onExpandedChange={onTrainingExpanded}
				/>
			</div>
		</>
	);
};

Summary.propTypes = {
	person: PropTypes.object,
	organisationId: PropTypes.string,
	profile: PropTypes.object.isRequired,
	organisations: PropTypes.array.isRequired,
	staffList: PropTypes.array.isRequired,
	onEdit: PropTypes.func.isRequired,
	onAddPhotoNote: PropTypes.func,
};

export default Summary;
